import * as React from 'react';
import Table from '@mui/material/Table';
import { styled } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { observer } from 'mobx-react-lite';
import { format } from 'date-fns';
import {
    Box, Card, CardActions, CardContent, CardHeader, Checkbox, Collapse,
    Grid, IconButton, IconButtonProps, Link, Stack, Typography, useMediaQuery
} from '@mui/material';
import { useState } from 'react';
import SkeltonAnimation from '../../../../app/layout/SkeltonAnimation';
import { useStore } from '../../../../app/stores/store';
import { KoDataErasureReport } from '../../../../app/models/dataErasure';
import KoReportForm from './KoReportForm';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import agent from '../../../../app/api/agent';
import MailIcon from '@mui/icons-material/Mail';
import EmailModal from '../../../../app/common/emailModal/EmailModal';
import { isTbd } from '../../../../app/common/options/flagOpt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withNavbar } from '../../../../app/layout/HOC';
import GenerateReportHeading from '../../../../app/common/reportsHeading/GenerateReportHeading';
import { koReportingContainerStyles } from './koReportingContainerStyles';
import { co2, collectionDate, country, customer, pickupNumber, receiveT1a, registrations, site, upcycleRecycle } from '../../../../app/common/options/koReportingOpt';
import { toast } from 'react-toastify';
import { mainStyles } from '../../../mainStyles';
import { adminUserStyles } from '../../../admin/adminUsers/adminUserStyles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#fcfcfc',
        fontSize: 16,
        fontWeight: 300,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        fontWeight: 300,
        fontStrethed: 'expanded',
    },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({

    '&:nth-of-type(odd)': {
        backgroundColor: '#DAE5F1',
    },
    '&:nth-of-type(even)': {
        backgroundColor: '#fcfcfc',
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const gridContainer = {
    xs: 1,
    sm: 2,
    md: 3
} as const;

const textGen = (title: string, value: string, xs: number, sm: number) => {
    return <Grid item xs={xs} sm={sm} key={title}>
        <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
        >
            {title}&nbsp;{value || "---"}
        </Stack>
    </Grid>
}
const iconGen = (title: string, value: boolean, xs: number, sm: number) => {
    return <Grid item xs={xs} sm={sm} key={title}>
        <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
        >
            {title}&nbsp;
            {value === false ? <CloseIcon /> : <DoneIcon />}

        </Stack>
    </Grid>
}
interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}
const ExpandMore = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

const KoReporting = () => {

    const [first, setFirst] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [pickupNo, setPickupNo] = useState<string | null>(null);

    const { reportingStore, modalStore } = useStore();
    const { loadKoDataErasureReports,
        koDataErasureReportsLoading, koDataErasureCheckHandler,
        koDataErasureReportsFiltered, koDataErasureFilter,
        koDataErasureFilterReset, selectAllHandler, isSelectAll, sortKoReportingTable } = reportingStore;

    const matches = useMediaQuery('(min-width:900px)');

    const handlePickupNoCollapse = (pNo: string) => {
        if (pickupNo === pNo) {
            setPickupNo(null);
        } else {
            setPickupNo(pNo);
        }
    };
    const { openModal } = modalStore;

    React.useEffect(() => {
        if (!first && koDataErasureReportsFiltered.length < 1) {
            loadKoDataErasureReports();
            setFirst(true);
        }
    }, [first, koDataErasureReportsFiltered.length, loadKoDataErasureReports])

    const resetFormHandler = () => {
        koDataErasureFilterReset();
    }
    const checkBoxHandler = (pickupNo: string) => {
        if (pickupNo) {
            koDataErasureCheckHandler(pickupNo);
        }
    };

    const downloadTemplate = (selOption: string) => {
        if (koDataErasureReportsFiltered?.length > 0 && selOption) {
            const selectedReports = koDataErasureReportsFiltered.filter(x => x.isChecked).map(m => m.pickupNumber).join(',');
            let fileName = koDataErasureReportsFiltered.filter(x => x.isChecked).map(m => m.pickupNumber).join('-');
            fileName = "KO-999-" + fileName.replaceAll("KO-999", "");
            if (fileName.length > 100)
                fileName = fileName.substring(0, 50);
                
            if (selectedReports) {
                try {
                    setExcelLoading(true);
                    if (selOption === registrations) {
                        agent.KoReport.downloadRegisterSerialNumbers(selectedReports)
                            .then((res) => {
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', fileName + '.xlsx');
                                document.body.appendChild(link);
                                link.click();
                                setExcelLoading(false);
                            })
                            .catch((error) => {
                                toast.error('No Record Found')
                                setExcelLoading(false)
                            });
                    }
                    if (selOption === upcycleRecycle) {
                        agent.KoReport.downloadUpCycleRecycleReport(selectedReports)
                            .then((res) => {
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', fileName + '.xlsx');
                                document.body.appendChild(link);
                                link.click();
                                setExcelLoading(false);
                            })
                            .catch((error) => {
                                toast.error('No Record Found')
                                setExcelLoading(false)
                            });
                    }
                    if (selOption === co2) {
                        agent.KoReport.downloadCo2Report(selectedReports)
                            .then((res) => {
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', fileName + '.xlsx');
                                document.body.appendChild(link);
                                link.click();
                                setExcelLoading(false);
                            })
                            .catch((error) => {
                                toast.error('No Record Found')
                                setExcelLoading(false)
                            });
                    }

                } catch (error) {
                    setExcelLoading(false);
                }
            }
        }
    }

    return (
        <Box component='div' sx={{ mt: 25 }}>

            {koDataErasureReportsLoading && <SkeltonAnimation iteration={10} />}
            {!koDataErasureReportsLoading &&
                <GenerateReportHeading heading='Settled' />
            }
            {!koDataErasureReportsLoading &&
                <KoReportForm koDataErasureFilter={koDataErasureFilter}
                    resetFormHandler={resetFormHandler} isChecked={koDataErasureReportsFiltered.some(x => x.isChecked)}
                    downloadTemplate={downloadTemplate} excelLoading={excelLoading} />
            }
            {!koDataErasureReportsLoading && koDataErasureReportsFiltered.length < 1 &&
                <Typography variant='h4' textAlign='center' sx={{ mt: 5, color: '#072B02' }} >No records found</Typography>
            }
            {/* Desktop View */}
            {matches && !koDataErasureReportsLoading && koDataErasureReportsFiltered.length > 0 &&
                <Box component='div'>
                    <Typography variant='body2' textAlign='center' sx={{ m: 2, color: '#072B02' }} >
                        RA:Report Available,  FDA:Financial Document Available,  Approved:Approved by Customer
                    </Typography>
                    <TableContainer component={Paper} sx={koReportingContainerStyles.table}>
                        <Table aria-label="CollectionReport table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>
                                        <Checkbox
                                            checked={isSelectAll}
                                            value={isSelectAll}
                                            onChange={selectAllHandler}
                                            inputProps={{ 'aria-label': 'allControlled' }}
                                            sx={adminUserStyles.adminCheckBoxStyle}
                                        /></StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() => sortKoReportingTable(pickupNumber)}>Pickup No</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() => sortKoReportingTable(customer)} >Customer</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() => sortKoReportingTable(site)}>Site</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() => sortKoReportingTable(country)}>Country</StyledTableCell>
                                    <StyledTableCell>Reference</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() => sortKoReportingTable(collectionDate)}>Collection</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() => sortKoReportingTable(receiveT1a)} align="center">Received</StyledTableCell>
                                    <StyledTableCell align="center">Progress</StyledTableCell>
                                    <StyledTableCell align="center">RA</StyledTableCell>
                                    <StyledTableCell align="center">FDA</StyledTableCell>
                                    <StyledTableCell align="right">Approved</StyledTableCell>
                                    <StyledTableCell align="right">Email</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {koDataErasureReportsFiltered.map((report: KoDataErasureReport) => {
                                    return (<StyledTableRow
                                        key={report.pickupNumber}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <StyledTableCell>
                                            <Checkbox
                                                checked={report.isChecked}
                                                value={report.isChecked}
                                                onChange={() => checkBoxHandler(report.pickupNumber)}
                                                inputProps={{ 'aria-label': report.pickupNumber }}
                                                sx={adminUserStyles.adminCheckBoxStyle}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.pickupNumber}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.customer}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.customerCity}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.country}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.reference}
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ cursor: 'pointer' }} component="th" scope="row">
                                            {isTbd(report.collectionDate.toString()) ? "TBD" :
                                                format(new Date(report.collectionDate), 'yyyy-MM-dd')
                                            }
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {isTbd(report.receivedT1A.toString()) ? "TBD" :
                                                format(new Date(report.receivedT1A), 'yyyy-MM-dd')
                                            }
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {report.progress}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {report.reportAvailable === true ? <DoneIcon /> : <CloseIcon />}
                                        </StyledTableCell>
                                        <StyledTableCell align='right'>
                                            {report.sbiAvailable === false ? <CloseIcon /> : <DoneIcon />}
                                        </StyledTableCell>
                                        <StyledTableCell align='right'>
                                            {report.sbiApproved === false ? <CloseIcon sx={{ color: 'red' }} /> : <DoneIcon sx={{ color: 'green' }} />}
                                        </StyledTableCell>

                                        <StyledTableCell align='right' sx={{ cursor: 'pointer' }}>
                                            <Link onClick={() => openModal(<EmailModal subject={report.pickupNumber} header='KO Reporting' type='REPORTING' />)} >
                                                <MailIcon /></Link>
                                        </StyledTableCell>
                                    </StyledTableRow>)
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            }
            {/* Mobile View */}
            {!matches && !koDataErasureReportsLoading && koDataErasureReportsFiltered.length > 0 &&
                <Box sx={{ flexGrow: 1, mt:4 }}>
                    <Grid container spacing={2} direction="column" justifyContent="center"
                        alignItems="center">
                        {koDataErasureReportsFiltered.map((rept: KoDataErasureReport) => {
                            return (
                                <Grid item xs={12} key={rept.pickupNumber}
                                    sx={{
                                        position: 'relative', pb: 0.5, pr: 0.5, mt: 2,
                                        bgcolor: '#000',
                                        borderRadius: '100% 0% 29% 71% / 0% 100% 0% 100% '
                                    }} >
                                    <Typography component='span' variant='body2'
                                        sx={{ position: 'absolute', right: 6, top: 0.1, color: '#fff' }}>
                                        Tier1Asset
                                    </Typography>
                                    <Card sx={mainStyles.cardStyles}>
                                        <CardHeader
                                            action={
                                                <Checkbox
                                                    checked={rept.isChecked}
                                                    value={rept.isChecked}
                                                    onChange={() => checkBoxHandler(rept.pickupNumber)}
                                                    inputProps={{ 'aria-label': rept.pickupNumber }}
                                                    sx={{

                                                        '& .MuiSvgIcon-root':
                                                            { fontSize: 28 }
                                                    }}
                                                />
                                            }
                                            title={rept.pickupNumber}
                                            subheader={rept.customer}
                                        />
                                        <CardContent>
                                            <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                                                {textGen('Customer City', rept.customerCity, 12, 6)}
                                                {textGen('Country', rept.country, 12, 6)}
                                            </Grid>
                                        </CardContent>
                                        <CardActions disableSpacing>
                                            <IconButton aria-label="share" onClick={() => openModal(<EmailModal subject={rept.pickupNumber} type='REPORTING'
                                                header='KO Reporting' />)}>
                                                <MailIcon />
                                            </IconButton>
                                            <ExpandMore
                                                expand={pickupNo === rept.pickupNumber}
                                                onClick={() => handlePickupNoCollapse(rept.pickupNumber)}
                                                aria-expanded={pickupNo === rept.pickupNumber}
                                                aria-label="More info"
                                            >
                                                <ExpandMoreIcon />
                                            </ExpandMore>
                                        </CardActions>
                                        <Collapse in={pickupNo === rept.pickupNumber} timeout="auto" unmountOnExit>
                                            <CardContent>
                                                <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                                                    {textGen('Collection Date:', isTbd(rept.collectionDate.toString()) ? "TBD" :
                                                        format(new Date(rept.collectionDate), 'yyyy-MM-dd')
                                                        , 12, 6)}
                                                    {iconGen('Report Available', rept.reportAvailable, 12, 6)}
                                                    {textGen('Recieved T1A:', isTbd(rept.receivedT1A.toString()) ? "TBD" :
                                                        format(new Date(rept.receivedT1A), 'yyyy-MM-dd')
                                                        , 12, 6)}
                                                    {iconGen('SBI Available', rept.sbiAvailable, 12, 6)}
                                                    {textGen('Progress:', rept.progress.toString(), 12, 6)}
                                                    {iconGen('SBI Approved ', rept.sbiApproved, 12, 6)}
                                                </Grid>
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                </Box>
            }
        </Box>
    );
}
const KoReportingPage = withNavbar(observer(KoReporting));
export default KoReportingPage;